import React, { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { Icon } from '../../../../shared/ui';
import { IconMap } from '../../../../shared/sprite';
import { useCollaboration } from '../../../../entities/collaboration';

type Props = {
  allowedTypes: string[];
  progress?: number;
  isUploading?: boolean;
  disabled?: boolean;
  maxSize: string;
  fileName: string;
  onValidateAndUpload(file?: File): void;
  clickHandler(): void;
};

export const PageCoverUploadInput: FC<Props> = ({
  progress,
  isUploading,
  disabled = false,
  fileName,
  onValidateAndUpload,
  clickHandler,
}) => {
  const { alertBarData } = useCollaboration();

  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const enterListener = (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const leaveListener = () => {
      setIsDragging(false);
    };

    const dropListener = (e: DragEvent) => {
      setIsDragging(false);

      e.preventDefault();

      const file = e.dataTransfer?.files[0];

      return onValidateAndUpload(file);
    };
    const containerRefElement = containerRef.current;

    containerRefElement?.addEventListener('dragover', enterListener);
    containerRefElement?.addEventListener('dragleave', leaveListener);
    containerRefElement?.addEventListener('drop', dropListener, {});
    return () => {
      containerRefElement?.removeEventListener('dragleave', leaveListener);
      containerRefElement?.removeEventListener('dragover', enterListener);
      containerRefElement?.removeEventListener('drop', dropListener);
    };
  }, [onValidateAndUpload]);

  return (
    <div
      className={classNames('py-4 px-3.5 bg-gray-50 h-full border ', {
        'border-primary-500': isDragging,
        'border-base-black/[0.1]': !isDragging,
        'rounded-t-2xl': !alertBarData?.isActive,
      })}
    >
      <div className="w-full h-full relative" ref={containerRef}>
        {isUploading ? (
          <div className="relative overflow-hidden p-4 pl-16 h-full flex rounded-xl items-center justify-center border border-dashed border-gray-400 bg-base-white">
            <div
              style={{ transform: `translateX(${progress}%)` }}
              className="absolute top-0 -left-full w-full h-full bg-gray-50"
            ></div>
            <div className="relative w-full text-gray-700">
              <p className="truncate">{fileName}</p>

              {progress === 100 ? (
                <p>Processing...</p>
              ) : (
                <p>{progress}% uploaded</p>
              )}
            </div>
          </div>
        ) : (
          <div
            onClick={clickHandler}
            className={classNames(
              'rounded-xl flex items-center flex-col text-gray-600 justify-center h-full relative',
              {
                'cursor-pointer': !disabled,
                'shadow-border-gray bg-gray-50 cursor-default': disabled,
              }
            )}
          >
            <Icon
              glyph={IconMap.Image01}
              className="text-gray-600 mb-2"
              width={20}
            />
            <span className="text-sm text-gray-600 text-center">
              Drag and drop image
            </span>
            <span className="text-sm text-gray-600 text-center">
              or tap for option
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
